import { useEffect, useRef, useState } from "react";
import { CheckBox, Note, Page } from "../../../../helbest";
import { useDispatch, useSelector } from "react-redux";
import HeaderBack from "../../../../helbest/HeaderBack/HeaderBack";
import styles from "./SelectService.module.css";
import {
  set_services_order_lines,
  reset_service_state,
  set_services_qr_meta,
} from "../../../../store/actions/services_actions";
import Steps from "../Steps/Steps";
import { Comma, dateFormatter } from "../../../../base/utils";
import { API } from "../../../../request/api";
import { BaseURL } from "../../../../base/env";
import { Verification } from "../Verification/Verification";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { Button, Input } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import Loading from "../../../components/Loading/Loading";
import { ReactComponent as CalendarIcon } from "../../../../assets/svg/calendar-1.svg";
import { useQuery } from "@apollo/client";
import { MessagingServices } from "../../../../graphql/none_saleor/otp/otp";
import { useLangNavigate } from "../../../../hooks/useLangNavigate";

// Preset extra-charge amounts the customer can be billed on top of the
// selected services, shown as quick-pick buttons alongside "no extra charge".
const EXTRA_CHARGE_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

const SelectService = () => {
  const dispatch = useDispatch();
  const navigate = useLangNavigate();
  const {
    phone_number,
    order_lines,
    odometer_value,
    user_car_id,
    is_coupon,
    user_car_name,
    skip_step,
    qr_order_lines,
    qr_token,
    qr_lat,
    qr_lng,
    qr_bill_id,
  } = useSelector((state) => state.Services);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [serviceData, setServiceData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState([]);
  const [cashCollected, setCashCollected] = useState(false);
  const [extraCharge, setExtraCharge] = useState(0);
  const [hasSkippedServiceSelection, setHasSkippedServiceSelection] = useState(false);
  const extraChargeScrollRef = useRef(null);
  const startSentinelRef = useRef(null);
  const endSentinelRef = useRef(null);
  // "Start"/"end" instead of "left"/"right": direction-agnostic so this
  // reads correctly in both LTR and RTL (Arabic) without any sign-flipping.
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);
  const { t } = useTranslation();

  // Scrolls the extra-charge row one "page" toward the start or end edge.
  // Uses the row's actual computed direction (rather than assuming LTR) so
  // the scroll direction is correct in RTL locales too, where scrollLeft's
  // sign/zero-point convention is inconsistent across browsers.
  const scrollExtraCharges = (towardEnd) => {
    const el = extraChargeScrollRef.current;
    if (!el) return;
    const isRTL = getComputedStyle(el).direction === "rtl";
    const sign = isRTL ? -1 : 1;
    el.scrollBy({ left: (towardEnd ? 1 : -1) * sign * 180, behavior: "smooth" });
  };

  useEffect(() => {
    if (step !== 2) return;
    const container = extraChargeScrollRef.current;
    const startEl = startSentinelRef.current;
    const endEl = endSentinelRef.current;
    if (!container || !startEl || !endEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === startEl) setCanScrollStart(!entry.isIntersecting);
          if (entry.target === endEl) setCanScrollEnd(!entry.isIntersecting);
        });
      },
      { root: container, threshold: 0 }

    );

    observer.observe(startEl);
    observer.observe(endEl);
    return () => observer.disconnect();
    // eslint-disable-next-line
  }, [step, serviceData]);

  const { loading: servicesLoading, refetch: getAvailableOtp } = useQuery(
    MessagingServices,
    {
      context: {
        clientName: "otp",
      },
      onError: (error) => {
        Note.error(error.message);
      },
      skip: true,
    }
  );

  // QR flow (skip_step: false — non-carwash scan): a bill doesn't exist yet.
  // Creates it using whichever order line ids are selected. No-op (resolves
  // true) for the normal flow and for the skip_step: true QR flow, where a
  // bill either isn't needed here or already exists.
  const createQrBillIfNeeded = async (orderLineIds) => {
    if (!(qr_token && !qr_bill_id)) return true;

    setSubmitLoading(true);
    try {
      const response = await API.post.qrLocation({
        token: qr_token,
        lat: qr_lat,
        long: qr_lng,
        user_car_id,
        current_kilometer: odometer_value,
        order_line_id: orderLineIds,
        extra_charge: extraCharge
      });

      if (response.success || response.code === 200) {
        dispatch(
          set_services_qr_meta({ qr_bill_id: response.data.bill_id })
        );
        return true;
      }

      Note.error(response.message);
      return false;
    } catch (err) {
      Note.error(t("error_boundary.message"));
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    // QR flow, fully resolved (skip_step: true): the bill/order lines are
    // already known from the scan, so there's no need to hit couponOrders —
    // build a single synthetic category from qr_order_lines and jump
    // straight to the review step.
    if (skip_step === true) {
      const mappedOrders = (qr_order_lines || []).map((o) => ({
        order_line_id: o.order_line_id,
        order_name: o.order_name,
        quantity: o.quantity,
        total_amount: o.total_amount,
        paid_online: o.paid_online,
        cash_payable_amount: o.cash_payable_amount,
        cash_paid: o.cash_paid,
      }));

      setServiceData([
        { category: { id: "qr", name: "" }, orders: mappedOrders },
      ]);
      setSelectedCategory("qr");
      const ids = mappedOrders.map((o) => o.order_line_id);
      setSelectedService(ids);
      dispatch(set_services_order_lines(ids));
      setStep(2);
      return;
    }

    // Normal flow (including QR flow with skip_step: false, which lands here
    // via NewService's customer-info screen first): fetch categories as usual.
    setLoading(true);
    API.get
      .couponOrders(phone_number.replace("+", ""))
      .then(async (response) => {
        if (response.success) {
          setServiceData(response.data);

          const allOrders = response.data.flatMap(
            (categoryItem) => categoryItem.orders
          );
          if (allOrders.length === 1) {
            const onlyOrder = allOrders[0];
            setHasSkippedServiceSelection(true);
            const onlyCategory = response.data.find((categoryItem) =>
              categoryItem.orders.some(
                (order) => order.order_line_id === onlyOrder.order_line_id
              )
            );
            setSelectedCategory(onlyCategory?.category.id);
            setSelectedService([onlyOrder.order_line_id]);
            dispatch(set_services_order_lines([onlyOrder.order_line_id]));

            // QR flow (skip_step: false) still needs its bill created before
            // advancing — same requirement as the manual path in onSubmit.
            const ok = await createQrBillIfNeeded([onlyOrder.order_line_id]);
            if (ok) {
              setStep(2);
            }
          } else {
            setHasSkippedServiceSelection(false);
            setSelectedCategory(response.data[0]?.category.id);
          }
        } else {
          Note.error(response.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!phone_number && !odometer_value && !user_car_id && !user_car_name) {
      navigate("/new-service/");
    }
    // eslint-disable-next-line
  }, [phone_number, odometer_value]);

  const onSubmit = async () => {
    if (!selectedCategory) {
      return Note.error(t("select_service.errors.select_category"));
    }

    if (!selectedService.length) {
      return Note.error(t("select_service.errors.select_service"));
    }

    // QR flow, bill not yet created (skip_step: false — non-carwash scan):
    // now that the user has actually picked which order line(s) to bill,
    // create the bill using that real selection instead of relying on the
    // backend's fallback to whichever line was originally scanned.
    const ok = await createQrBillIfNeeded(selectedService);
    if (!ok) return;

    setStep(2);
  };

  const onSubmitServiceInfo = () => {
    setSubmitLoading(true);

    getAvailableOtp().then((availableResponse) => {
      if (availableResponse.data.messagingServices) {
        const services = availableResponse.data.messagingServices;
        const messaging_service =
          services.find((service) => service.isDefault)?.value ||
          services[0].value;
        API.post
          .serviceInfo(
            phone_number,
            order_lines,
            odometer_value,
            user_car_id,
            is_coupon,
            messaging_service,
            extraCharge,
          )
          .then((response) => {
            if (response.success) {
              navigate(
                `/services/verification/${response.data.bill_id}/${phone_number}`,
                {
                  state: {
                    availableOtp: services,
                  },
                }
              );
            } else {
              Note.error(response.message);
            }
          })
          .finally(() => {
            setSubmitLoading(false);
          });
      }
    });
  };

  // QR flow: at this point the bill already exists — either created at scan
  // time (carwash, skip_step: true) or created above via createQrBillIfNeeded
  // (non-carwash, skip_step: false). This call is purely the "verify" phase:
  // backend just needs bill_id.
  const onConfirmQrService = () => {
    setSubmitLoading(true);
    API.post
      .qrLocation({
        token: qr_token,
        lat: qr_lat,
        long: qr_lng,
        bill_id: qr_bill_id,
        extra_charge: extraCharge,
      })
      .then((response) => {
        if (response.success || response.code === 200) {
          navigate("/success-qr", { state: { status: "done" } });
        } else {
          Note.error(response.message);
        }
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  // "Leaving this screen back to customer-info" means different things
  // depending on how this session started:
  // - skip_step: true (carwash QR scan) never legitimately passed through
  //   NewService's customer-info screen — there's nothing to go back to,
  //   so treat as a full reset back to the phone/QR entry point.
  // - Everything else (normal flow, or skip_step: false QR flow that DID
  //   pass through customer-info) should preserve all session state and
  //   just navigate back to review/adjust it.
  const goBackToCustomerInfo = () => {
    if (skip_step === true) {
      dispatch(reset_service_state());
    }
    navigate("/new-service/");
  };

  // "Back" is context-dependent:
  // - On step 2 of the normal/QR-with-customer-info flow, back just returns
  //   to step 1 (service selection) — no navigation needed.
  // - On step 1, or on step 2 of the skip_step:true QR flow (which has no
  //   step 1 to return to), fall back to leaving the page entirely.
  const handleBack = () => {
    if (step === 2 && skip_step !== true) {
      if (hasSkippedServiceSelection) {
        goBackToCustomerInfo();
        return;
      }
      setStep(1);
      return;
    }
    goBackToCustomerInfo();
  };

  const selectedOrderItems = serviceData
    .flatMap((serviceItem) => serviceItem.orders || [])
    .filter((orderItem) => selectedService?.includes(orderItem.order_line_id));


  const totalOnlineAmount =
    selectedOrderItems.reduce(
      (sum, orderItem) => sum + Number(orderItem.paid_online || 0),
      0
    );

  // Sum of each line's cash-payable portion — drives whether the
  // cash-collected checkbox appears and the amount shown in its label.
  const totalCashAmount = selectedOrderItems.reduce(
    (sum, orderItem) => sum + Number(orderItem.cash_payable_amount || 0),
    0
  ) + extraCharge;

  // Full price across all selected lines (online + cash portions) plus
  // extra charge — shown as the standalone "Total amount" row.
  const totalAllAmount =
    selectedOrderItems.reduce(
      (sum, orderItem) => sum + Number(orderItem.total_amount || 0),
      0
    ) + extraCharge;

  const hasCashPayment = selectedOrderItems.some(
    (orderItem) => !orderItem.cash_paid
  );

  const renderOrderName = (name) => {
    const plusIndex = name.indexOf("+");
    if (plusIndex === -1) {
      return (
        <span className={styles.ItemName}>
          <span className={styles.ItemNameBullet}>•</span>
          <span>{name}</span>
        </span>
      );
    }

    const first = name.slice(0, plusIndex + 1).trim();
    const rest = name.slice(plusIndex + 1).trim();

    return (
      <span className={styles.ItemName}>
        <span className={styles.ItemNameBullet}>•</span>
        <span>
          <span>{first} <span className={styles.ItemNameExtra}>{rest}</span></span>  
        </span>
      </span>
    );
  };

  return (
    <Page
      className="bg-gray-light"
      bodyWrapperClassName={styles.Body}
      showFooter={step === 1 || step === 2}
      customHeader={
        <HeaderBack title={t("drawer.new_service")} onClick={handleBack} />
      }
      extra_header={<Steps
        step={2}
        open
        hasShadow
        disableCustomerData={skip_step === true}
      />}
      customPageClassName={styles.SelectServicePage}

      footer={
        <div className="page-fixed-footer-container">
          {step === 1 ? (
            <Button
              type="primary"
              size="large"
              style={{
                width: "100%",
              }}
              disabled={!selectedService.length}
              loading={submitLoading}
              onClick={onSubmit}
            >
              {t("common.next")}
            </Button>
          ) : step === 2 ? (
            <div
              className="w-full flex justify-between items-center"
              style={{ gap: "12px" }}
            >
              <Button
                className={`flex-stretch ${styles.Button}`}
                type="primary"
                ghost={true}
                onClick={handleBack}
              >
                {t("common.cancel")}
              </Button>
              <Button
                className={`flex-stretch ${styles.Button}`}
                type="primary"
                loading={submitLoading || servicesLoading}
                disabled={hasCashPayment && !cashCollected}
                onClick={qr_token ? onConfirmQrService : onSubmitServiceInfo}
              >
                {t("common.confirm")}
              </Button>
            </div>
          ) : null}
        </div>

      }
      headerFix
      logo
      back
    >
      {step === 1 && loading ? (
        <div className="text-center mt-5">
          <Loading />
        </div>
      ) : null}
      {step === 1 && !loading ? (
        <>
          <div className={styles.ProductListContainer}>
            <div className={styles.ProductList}>
              {serviceData?.map((service) => {
                return (
                  <div
                    key={service.category.id}
                    className={styles.ProductItem}
                    onClick={() => setSelectedCategory(service.category.id)}
                  >
                    <div className={`${styles.ProductItemImageContainer}`}>
                      {service.category.image && (
                        <img
                          alt={service.category.name}
                          src={service.category.image}
                          className={styles.ProductItemImage}
                        />
                      )}
                      {service.service_count > 0 && (
                        <div className={styles.ProductItemsCount}>
                          {service.service_count}
                        </div>
                      )}
                    </div>
                    <span
                      className={`${styles.ProductItemText} ${selectedCategory === service.category.id
                        ? styles.Active
                        : ""
                        }`}
                    >
                      {service.category.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ background: "#FAFAFA" }}>
            <p className={styles.servicesLabel}>
              {t("select_service.select_products")}
            </p>
            <p className={styles.servicesDescription}>
              {t("select_service.select_products_description")}
            </p>
            <div className={styles.ServicesContainer}>
              <div className={styles.Services}>
                {serviceData
                  .find((service) => service.category.id === selectedCategory)
                  ?.orders?.map((order) => {
                    const checked = selectedService.includes(
                      order.order_line_id
                    );
                    return (
                      <label
                        className={`${styles.serviceItem} ${checked ? styles.checked : ""
                          } ${order.expired_at ? styles.expired : ""}`}
                      >
                        <div className={styles.serviceItemCheckbox}>
                          <CheckBox
                            checked={checked}
                            onChange={(e) => {
                              setSelectedService((prevState) => {
                                const newState = e.target.checked
                                  ? [...prevState, order.order_line_id]
                                  : prevState.filter(
                                    (id) => id !== order.order_line_id
                                  );
                                dispatch(set_services_order_lines(newState));
                                return newState;
                              });
                            }}
                          />
                        </div>
                        <div className={styles.ServiceDetails}>
                          <div className={styles.ServiceType}>
                            <span>{t("orderdetails.service_type")}</span>
                            <span>{order.delivery_type}</span>
                          </div>
                          <div className={styles.ServiceType}>
                            <span>{t("service_details.product_name")}</span>
                            <span>{order.order_name}</span>
                          </div>
                          <div className={styles.ServiceType}>
                            <span>{t("orderdetails.quantity")}</span>
                            <span>{order.quantity}</span>
                          </div>
                          <div className={styles.ServiceType}>
                            <span>{t("orderdetails.price")}</span>
                            <span>{order.cash_payable_amount}</span>
                          </div>
                          {order.expired_at && (
                            <div
                              className={`${styles.ServiceType} ${styles.expired}`}
                            >
                              <span>{t("orderdetails.expired_at")}</span>
                              <CalendarIcon
                                width={16}
                                height={16}
                                style={{
                                  marginInlineStart: "auto",
                                }}
                              />
                              <span>
                                {dateFormatter(order.expired_at, {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
              </div>
            </div>
          </div>
        </>
      ) : null}

      {step === 2 && (
        <div className={styles.SelectedServiceBoxContainer}>

          <p className={styles.extraChargeLabel}>
            {t("new_service.choose_additional_amount")}
          </p>
          <div className={styles.ExtraChargeOptions}>
            <button
              type="button"
              className={`${styles.ExtraChargeButton} ${styles.ExtraChargeButtonFull} ${extraCharge === 0 ? styles.ExtraChargeButtonActive : ""
                }`}
              onClick={() => setExtraCharge(0)}
            >
              {t("new_service.no_additional_amount")}
            </button>
            <div
              className={styles.ExtraChargeScrollWrapper}
              style={{
                paddingInlineStart: canScrollStart ? 28 : 0,
                paddingInlineEnd: canScrollEnd ? 28 : 0,
              }}
            >

              {canScrollStart && (
                <button
                  type="button"
                  className={`${styles.ExtraChargeScrollButton} ${styles.ExtraChargeScrollButtonStart}`}
                  aria-label="Scroll additional amounts backward"
                  aria-controls="extra-charge-options"
                  onClick={() => scrollExtraCharges(false)}
                >
                  <LeftOutlined className={styles.ExtraChargeScrollIcon} />
                </button>
              )}
              <div
                id="extra-charge-options"
                ref={extraChargeScrollRef}
                className={styles.ExtraChargeButtonRow}
                role="region"
                aria-label="Additional amount options"
                tabIndex={0}
              >
                <span
                  ref={startSentinelRef}
                  className={styles.ExtraChargeSentinel}
                  aria-hidden="true"
                  style={{ width: 1, flex: "0 0 auto" }}
                />
                {EXTRA_CHARGE_OPTIONS.map((amount) => (
                  <button
                    type="button"
                    key={amount}
                    className={`${styles.ExtraChargeButton} ${extraCharge === amount ? styles.ExtraChargeButtonActive : ""
                      }`}
                    onClick={() => setExtraCharge(amount)}
                  >
                    {amount} <span className={styles.ExtraCurrencyUnit}>OMR</span>
                  </button>
                ))}
                <span
                  ref={endSentinelRef}
                  className={styles.ExtraChargeSentinel}
                  aria-hidden="true"
                  style={{ width: 1, flex: "0 0 auto" }}
                />
              </div>
              {canScrollEnd && (
                <button
                  type="button"
                  className={`${styles.ExtraChargeScrollButton} ${styles.ExtraChargeScrollButtonEnd}`}
                  aria-label="Scroll additional amounts forward"
                  aria-controls="extra-charge-options"
                  onClick={() => scrollExtraCharges(true)}
                >
                  <RightOutlined className={styles.ExtraChargeScrollIcon} />
                </button>
              )}
            </div>
          </div>

          <div className={styles.ItemsCard}>
            <div className={styles.ItemsHeaderRow}>
              <span className={styles.ItemsTitle}>{t("new_service.items")}:</span>
              <span className={styles.ItemsTitle}>{t("new_service.quantity")}</span>
              <span className={styles.ItemsTitle}>{t("new_service.price")}</span>
            </div>

            {selectedOrderItems.map((orderItem) => (
              <div
                key={orderItem.order_line_id}
                className={`${styles.ItemRow} ${styles.ItemRowDivider}`}
              >
                {renderOrderName(orderItem.order_name)}
                <span className={styles.ItemQuantity}>{orderItem.quantity}</span>
                <span className={styles.ItemPrice}>
                  {Number(orderItem.total_amount || 0).toFixed(3)}{" "}
                  <span className={styles.CurrencyUnit}>OMR</span>
                </span>
              </div>
            ))}

            <div className={`${styles.ItemRow} ${styles.ItemRowDividerDark}`}>
              <span className={styles.ItemName}>
                <span className={styles.ItemNameBullet}>•</span>
                <span>{t("new_service.additional_amount")}</span>
              </span>
              <span className={styles.ItemQuantity} />
              <span className={styles.ItemPrice}>
                {extraCharge.toFixed(3)}{" "}
                <span className={styles.CurrencyUnit}>OMR</span>
              </span>
            </div>

            <div className={styles.ItemRow}>
              <span className={`${styles.ItemName} ${styles.bold}`}>
                {t("new_service.total_amount")}
              </span>
              <span className={styles.ItemQuantity} />
              <span className={`${styles.ItemPrice} ${styles.bold}`}>
                {totalAllAmount.toFixed(3)}{" "}
                <span className={styles.CurrencyUnit}>OMR</span>
              </span>
            </div>

            <div className={styles.ItemRow}>
              <span className={`${styles.ItemName} ${styles.bold}`}>
                {t("new_service.paid_online")}
              </span>
              <span className={styles.ItemQuantity} />
              <span className={`${styles.ItemPrice} ${styles.bold}`}>
                {totalOnlineAmount.toFixed(3)}{" "}
                <span className={styles.CurrencyUnit}>OMR</span>
              </span>
            </div>

            <div className={styles.TotalRow}>
              <span>{t("new_service.cash_payable_amount")}</span>
              <span className={styles.TotalAmount}>
                {totalCashAmount.toFixed(3)}{" "}
                <span className={styles.CurrencyUnit}>OMR</span>
              </span>
            </div>
          </div>
        </div>

      )
      }
      {step === 2 && hasCashPayment && (
        <div className={styles.CashCollectedContainer}>
          <label className={styles.CashCollectedRow}>
            <CheckBox
              checked={cashCollected}
              onChange={(e) => setCashCollected(e.target.checked)}
            />
            <span>
              <Trans
                i18nKey="new_service.collected_cash_payment"
                values={{ amount: totalCashAmount.toFixed(3) }}
                components={{ bold: <strong /> }}
              />
            </span>
          </label>
        </div>
      )}

      {step === 4 && <Verification />}
    </Page >
  );
};

export default SelectService;